import React, { useState, useCallback, useRef, useEffect } from "react";
import "./searchBarIntroSection.css";
import { useNavigate, useLocation } from "react-router-dom";
import SearchBarFilterButton from "../../../../components/Buttons/SearchBarFilterButton/SearchBarFilterButton";
import MunicipalityDropdown from "../../../AdPage/Dropdowns/MunicipalityDropdown/MunicipalityDropdown";
import SpaceDropdown from "../../../AdPage/Dropdowns/SpaceDropdown/SpaceDropdown";
import PriceDropdown from "../../../AdPage/Dropdowns/PriceDropdown/PriceDropdown";
import useFilters from "../../../AdPage/utils/hooks/useFilters";
import RoundedButton from "../../../../components/Buttons/RoundedButton/RoundedButton";

export default function SearchBarIntroSection() {
  const navigate = useNavigate();
  const location = useLocation();
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = useRef([]);

  const {
    searchTerm,
    selectedMunicipality,
    selectedRoomTypes,
    selectedPriceRange,
    setSearchTerm,
    setSelectedMunicipality,
    setSelectedRoomTypes,
    setSelectedPriceRange,
    selectedZones,
    setSelectedZones,
  } = useFilters(location);

  const closeAllDropdowns = useCallback(() => {
    setOpenDropdown(null);
  }, []);

  const toggleDropdown = useCallback((dropdownName) => {
    setOpenDropdown((prev) => (prev === dropdownName ? null : dropdownName));
  }, []);

  const handleOutsideClick = useCallback(
    (event) => {
      let isClickInsideDropdown = false;

      Object.values(dropdownRefs.current).forEach((ref) => {
        if (ref && ref.contains(event.target)) {
          isClickInsideDropdown = true;
        }
      });

      if (!isClickInsideDropdown) {
        closeAllDropdowns();
      }
    },
    [closeAllDropdowns],
  );

  useEffect(() => {
    if (openDropdown) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [openDropdown, handleOutsideClick]);

  const handleSearch = (e) => {
    e.preventDefault();

    if (
      selectedMunicipality.length === 0 &&
      selectedRoomTypes.length === 0 &&
      !searchTerm &&
      selectedPriceRange.min === null &&
      selectedPriceRange.max === null
    ) {
      navigate("/lediga-lokaler");
      return;
    }

    let basePath = "/lediga-lokaler";

    if (selectedMunicipality.length > 0 || selectedRoomTypes.length > 0) {
      const municipalityPath =
        selectedMunicipality.length > 0
          ? `/${selectedMunicipality.map((m) => m.toLowerCase()).join("-")}`
          : "/allakommuner";

      const roomTypesPath =
        selectedRoomTypes.length > 0
          ? `/${selectedRoomTypes.map((rt) => rt.replace(/&/g, "%26").toLowerCase()).join("-")}`
          : "";

      basePath = `/lediga-lokaler${municipalityPath}${roomTypesPath}`;
    }

    const params = new URLSearchParams();
    if (searchTerm) params.append("q", searchTerm);
    if (selectedPriceRange.min !== null)
      params.append("min-hyra", selectedPriceRange.min);
    if (selectedPriceRange.max !== null)
      params.append("max-hyra", selectedPriceRange.max);

    const queryString = params.toString();
    const fullPath = queryString ? `${basePath}?${queryString}` : basePath;

    navigate(fullPath);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch(e);
    }
  };

  const locationIcon = (
    <svg width="1.7rem" height="1.7rem" viewBox="0 0 24 24" fill="#4e8157">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.0036 14.0035C14.4889 14.0035 16.5036 11.9887 16.5036 9.50347C16.5036 7.01819 14.4889 5.00347 12.0036 5.00347C9.51831 5.00347 7.50359 7.01819 7.50359 9.50347C7.50359 11.9887 9.51831 14.0035 12.0036 14.0035ZM12.0036 12.0071C10.6209 12.0071 9.5 10.8862 9.5 9.50347C9.5 8.12077 10.6209 6.99988 12.0036 6.99988C13.3863 6.99988 14.5072 8.12077 14.5072 9.50347C14.5072 10.8862 13.3863 12.0071 12.0036 12.0071Z"
        fill="#4e8157"
      ></path>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M21.272 11.5818C22.5649 5.78816 18.0052 0.00354004 12 0.00354004C5.99649 0.00354004 1.43639 5.7961 2.72816 11.5825C3.72523 16.2821 7.7572 20.8465 10.1636 23.2269C11.1942 24.2463 12.8058 24.2463 13.8364 23.2269C16.2429 20.8464 20.2752 16.2816 21.272 11.5818ZM12 2.00354C16.7124 2.00354 20.3368 6.58981 19.32 11.1462C18.8316 13.3355 17.7359 15.3015 16.4501 17.119C15.1064 19.0184 13.5829 20.6644 12.4299 21.805C12.1786 22.0536 11.8214 22.0536 11.5701 21.805C10.4171 20.6645 8.89379 19.0186 7.55009 17.1193C6.26419 15.3017 5.16886 13.3361 4.68011 11.1467C3.66438 6.59682 7.29012 2.00354 12 2.00354Z"
        fill="#4e8157"
      ></path>
    </svg>
  );

  const roomTypeIcon = (
    <svg fill="#4e8157" width="2.1rem" height="2.1rem" viewBox="0 0 56 56">
      <path d="M3.5288801,45.3231258 C2.43762978,45.3231258 1.57666326,44.9723596 0.945980541,44.270827 C0.363838669,43.6232585 0.0503776619,42.7734225 0.00559751799,41.7213191 L0,41.4540794 L0,3.86908131 C0,2.67859475 0.315326847,1.73612623 0.945980541,1.04167574 C1.5281492,0.400644514 2.30653602,0.0554738558 3.28114098,0.00616376176 L3.5288801,0 L30.3358562,0 C31.4129423,0 32.2668268,0.347225246 32.8975095,1.04167574 C33.4796514,1.68270696 33.7931124,2.53506716 33.8378925,3.59875633 L33.84349,3.86908131 L33.843,10.459 L46.492192,10.4592063 C47.4864522,10.4592063 48.2905286,10.7580841 48.9044214,11.3558397 L49.0539324,11.5115052 C49.6361278,12.1590737 49.9496177,13.0089072 49.994402,14.0610056 L50,14.3282441 L50,41.4540794 C50,42.6303785 49.6846441,43.5692944 49.0539324,44.270827 C48.4717369,44.9183955 47.6993578,45.2670863 46.7367949,45.3168992 L46.492192,45.3231258 L3.5288801,45.3231258 Z M33.84349,41.4540794 C33.84349,41.6070996 33.8381539,41.7561027 33.8274818,41.9010887 L45.3654442,41.9005118 C45.7764372,41.9005118 46.081199,41.8013046 46.2797295,41.6028902 C46.4498985,41.4328232 46.547138,41.1794565 46.5714478,40.8427902 L46.5775253,40.6675154 L46.5775253,15.1147907 C46.5775253,14.6896335 46.47826,14.3778477 46.2797295,14.1794333 C46.1095605,14.0093638 45.861344,13.9121812 45.5350801,13.8878856 L45.3654442,13.8818116 L33.843,13.881 L33.84349,41.4540794 Z M29.2091519,3.42264885 L4.65558437,3.42264885 C4.23042718,3.42264885 3.91864138,3.52185606 3.72022695,3.72027049 C3.55015744,3.89034 3.45297487,4.14370276 3.42867922,4.48035879 L3.42260531,4.65562791 L3.42260531,40.6675154 C3.42260531,41.0926871 3.52181253,41.4044787 3.72022695,41.6028902 C3.89029646,41.7729597 4.14365923,41.8701423 4.48031525,41.8944379 L4.65558437,41.9005118 L9.523,41.9 L9.52380538,35.6292547 C9.52380538,34.7917984 9.69656368,34.1563867 10.0420803,33.7230198 L10.150918,33.5990711 C10.5309863,33.2060999 11.0955308,32.991752 11.8445515,32.9560273 L12.074792,32.9506686 L21.768698,32.9506686 C22.6473697,32.9506686 23.2922021,33.1668028 23.7031951,33.5990711 C24.0768251,33.9920291 24.2806233,34.5870318 24.3145897,35.3840791 L24.3196846,35.6292547 L24.319,41.9 L29.2091519,41.9005118 C29.6201449,41.9005118 29.9248486,41.8013046 30.1232631,41.6028902 C30.2933326,41.4328232 30.3905152,41.1794565 30.4148108,40.8427902 L30.4208847,40.6675154 L30.4208847,4.65562791 C30.4208847,4.23047072 30.3216775,3.91868492 30.1232631,3.72027049 C29.9248486,3.52185606 29.6201449,3.42264885 29.2091519,3.42264885 Z M20.8545868,35.7355469 L12.9889467,35.7355469 C12.5766608,35.7355469 12.3517775,35.9229496 12.314297,36.297755 L12.3086749,36.4158187 L12.308,41.9 L21.534,41.9 L21.5348587,36.4158187 C21.5348587,36.0035328 21.3474559,35.7786495 20.9726505,35.741169 L20.8545868,35.7355469 Z M41.9216362,33.6309405 C42.3632989,33.6309405 42.6025328,33.8333924 42.639338,34.2382962 L42.6443569,34.353744 L42.6443569,37.6700693 C42.6443569,38.1117735 42.4419282,38.3510299 42.0370707,38.3878386 L41.9216362,38.392858 L38.4991615,38.392858 C38.0704294,38.392858 37.8381995,38.1904102 37.8024718,37.7855147 L37.7975999,37.6700693 L37.7975999,34.353744 C37.7975999,33.9120307 37.9941021,33.6727694 38.3871065,33.6359599 L38.4991615,33.6309405 L41.9216362,33.6309405 Z M41.9216362,25.5102147 C42.3632989,25.5102147 42.6025328,25.7126557 42.639338,26.1175375 L42.6443569,26.2329791 L42.6443569,29.5493096 C42.6443569,29.9910255 42.4419282,30.2302883 42.0370707,30.2670979 L41.9216362,30.2721174 L38.4991615,30.2721174 C38.0704294,30.2721174 37.8381995,30.0696643 37.8024718,29.6647581 L37.7975999,29.5493096 L37.7975999,26.2329791 C37.7975999,25.7912898 37.9941021,25.5520414 38.3871065,25.5152339 L38.4991615,25.5102147 L41.9216362,25.5102147 Z M14.0093327,23.7882672 C14.5457134,23.7882672 14.8345338,24.0358275 14.8757938,24.5309482 L14.8809513,24.6598859 L14.8809513,28.6777345 C14.8809513,29.2140884 14.633391,29.5028943 14.1382703,29.5441523 L14.0093327,29.5493096 L9.86391952,29.5493096 C9.35371482,29.5493096 9.07898921,29.3017616 9.0397427,28.8066657 L9.03483688,28.6777345 L9.03483688,24.6598859 C9.03483688,24.1235051 9.27031597,23.8346848 9.74127416,23.7934247 L9.86391952,23.7882672 L14.0093327,23.7882672 Z M23.9583243,23.7882672 C24.4946782,23.7882672 24.7834842,24.0358275 24.8247421,24.5309482 L24.8298994,24.6598859 L24.8298994,28.6777345 C24.8298994,29.2140884 24.5823514,29.5028943 24.0872555,29.5441523 L23.9583243,29.5493096 L19.8341574,29.5493096 C19.310878,29.5493096 19.0291123,29.3017616 18.98886,28.8066657 L18.9838285,28.6777345 L18.9838285,24.6598859 C18.9838285,24.1235051 19.225342,23.8346848 19.7083691,23.7934247 L19.8341574,23.7882672 L23.9583243,23.7882672 Z M41.9216362,17.3894455 C42.3632989,17.3894455 42.6025328,17.5918986 42.639338,17.9968049 L42.6443569,18.1122533 L42.6443569,21.4285839 C42.6443569,21.8702732 42.4419282,22.1095215 42.0370707,22.146329 L41.9216362,22.1513482 L38.4991615,22.1513482 C38.0704294,22.1513482 37.8381995,21.9489072 37.8024718,21.5440254 L37.7975999,21.4285839 L37.7975999,18.1122533 C37.7975999,17.6705374 37.9941021,17.4312746 38.3871065,17.394465 L38.4991615,17.3894455 L41.9216362,17.3894455 Z M14.0093327,15.3699198 C14.5457134,15.3699198 14.8345338,15.6174678 14.8757938,16.1125637 L14.8809513,16.241495 L14.8809513,20.2593436 C14.8809513,20.7957243 14.633391,21.0845447 14.1382703,21.1258047 L14.0093327,21.1309622 L9.86391952,21.1309622 C9.35371482,21.1309622 9.07898921,20.8834019 9.0397427,20.3882812 L9.03483688,20.2593436 L9.03483688,16.241495 C9.03483688,15.705141 9.27031597,15.4163351 9.74127416,15.3750771 L9.86391952,15.3699198 L14.0093327,15.3699198 Z M23.9583243,15.3699198 C24.4946782,15.3699198 24.7834842,15.6174678 24.8247421,16.1125637 L24.8298994,16.241495 L24.8298994,20.2593436 C24.8298994,20.7957243 24.5823514,21.0845447 24.0872555,21.1258047 L23.9583243,21.1309622 L19.8341574,21.1309622 C19.310878,21.1309622 19.0291123,20.8834019 18.98886,20.3882812 L18.9838285,20.2593436 L18.9838285,16.241495 C18.9838285,15.705141 19.225342,15.4163351 19.7083691,15.3750771 L19.8341574,15.3699198 L23.9583243,15.3699198 Z M14.0093327,6.95152895 C14.5457134,6.95152895 14.8345338,7.19908928 14.8757938,7.69420994 L14.8809513,7.82314761 L14.8809513,11.8409962 C14.8809513,12.3773769 14.633391,12.6661973 14.1382703,12.7074574 L14.0093327,12.7126149 L9.86391952,12.7126149 C9.35371482,12.7126149 9.07898921,12.4650545 9.0397427,11.9699339 L9.03483688,11.8409962 L9.03483688,7.82314761 C9.03483688,7.2867669 9.27031597,6.99794651 9.74127416,6.95668645 L9.86391952,6.95152895 L14.0093327,6.95152895 Z M23.9583243,6.95152895 C24.4946782,6.95152895 24.7834842,7.19908928 24.8247421,7.69420994 L24.8298994,7.82314761 L24.8298994,11.8409962 C24.8298994,12.3773769 24.5823514,12.6661973 24.0872555,12.7074574 L23.9583243,12.7126149 L19.8341574,12.7126149 C19.310878,12.7126149 19.0291123,12.4650545 18.98886,11.9699339 L18.9838285,11.8409962 L18.9838285,7.82314761 C18.9838285,7.2867669 19.225342,6.99794651 19.7083691,6.95668645 L19.8341574,6.95152895 L23.9583243,6.95152895 Z" />
    </svg>
  );

  const priceIcon = (
    <svg
      fill="#4e8157"
      width="2rem"
      height="2rem"
      viewBox="0 0 32 32"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
      <g
        id="SVGRepo_tracerCarrier"
        strokeLinecap="round"
        strokeLinejoin="round"
      ></g>
      <g id="SVGRepo_iconCarrier">
        {" "}
        <path d="M31,7H1A1,1,0,0,0,0,8V24a1,1,0,0,0,1,1H31a1,1,0,0,0,1-1V8A1,1,0,0,0,31,7ZM25.09,23H6.91A6,6,0,0,0,2,18.09V13.91A6,6,0,0,0,6.91,9H25.09A6,6,0,0,0,30,13.91v4.18A6,6,0,0,0,25.09,23ZM30,11.86A4,4,0,0,1,27.14,9H30ZM4.86,9A4,4,0,0,1,2,11.86V9ZM2,20.14A4,4,0,0,1,4.86,23H2ZM27.14,23A4,4,0,0,1,30,20.14V23Z"></path>{" "}
        <path d="M7.51.71a1,1,0,0,0-.76-.1,1,1,0,0,0-.61.46l-2,3.43a1,1,0,0,0,1.74,1L7.38,2.94l5.07,2.93a1,1,0,0,0,1-1.74Z"></path>{" "}
        <path d="M24.49,31.29a1,1,0,0,0,.5.14.78.78,0,0,0,.26,0,1,1,0,0,0,.61-.46l2-3.43a1,1,0,1,0-1.74-1l-1.48,2.56-5.07-2.93a1,1,0,0,0-1,1.74Z"></path>{" "}
        <path d="M16,10a6,6,0,1,0,6,6A6,6,0,0,0,16,10Zm0,10a4,4,0,1,1,4-4A4,4,0,0,1,16,20Z"></path>{" "}
      </g>
    </svg>
  );

  const priceCount =
    selectedPriceRange.min !== null || selectedPriceRange.max !== null ? 1 : 0;

  return (
    <div className="new-intro-section-form">
      <div className="intro-search-bar">
        <div className="input-div">
          <svg viewBox="0 0 512 512" fill="#4e8157" height="2rem" width="2rem">
            <path d="M416 208c0 45.9-14.9 88.3-40 122.7l126.6 126.7c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0s208 93.1 208 208zM208 352c79.5 0 144-64.5 144-144S287.5 64 208 64 64 128.5 64 208s64.5 144 144 144z" />
          </svg>{" "}
          <input
            className="intro-search-bar-input"
            type="text"
            placeholder="Sök..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyPress}
            aria-label="Sökfält för att söka lokal"
          />
        </div>
        <div className="intro-search-btns">
          <div className="intro-search-btns-filter-btns">
            <SearchBarFilterButton
              label="Kommun"
              count={selectedMunicipality.length + selectedZones.length}
              icon={locationIcon}
              ref={(el) => (dropdownRefs.current.municipality = el)}
              dropdownContent={
                <MunicipalityDropdown
                  selectedMunicipality={selectedMunicipality}
                  setSelectedMunicipality={setSelectedMunicipality}
                  selectedZones={selectedZones}
                  setSelectedZones={setSelectedZones}
                  closeMunicipalityDropdown={closeAllDropdowns}
                />
              }
              isDropdownVisible={openDropdown === "municipality"}
              toggleDropdown={() => toggleDropdown("municipality")}
              isActive={selectedMunicipality.length > 0}
            />
            <SearchBarFilterButton
              label="Lokaltyp"
              count={selectedRoomTypes.length}
              icon={roomTypeIcon}
              ref={(el) => (dropdownRefs.current.space = el)}
              dropdownContent={
                <SpaceDropdown
                  selectedRoomTypes={selectedRoomTypes}
                  setSelectedRoomTypes={setSelectedRoomTypes}
                  closeDropdown={closeAllDropdowns}
                />
              }
              isDropdownVisible={openDropdown === "space"}
              toggleDropdown={() => toggleDropdown("space")}
              isActive={selectedRoomTypes.length > 0}
            />
            <SearchBarFilterButton
              label="Pris"
              count={priceCount}
              icon={priceIcon}
              ref={(el) => (dropdownRefs.current.price = el)}
              dropdownContent={
                <PriceDropdown
                  selectedPriceRange={selectedPriceRange}
                  setSelectedPriceRange={setSelectedPriceRange}
                  closePriceDropdown={closeAllDropdowns}
                />
              }
              isDropdownVisible={openDropdown === "price"}
              toggleDropdown={() => toggleDropdown("price")}
              isActive={
                selectedPriceRange.min !== null ||
                selectedPriceRange.max !== null
              }
            />
          </div>
          <RoundedButton
            label="Sök lokaler"
            color="green"
            onClick={handleSearch}
            size="md"
          />
        </div>
      </div>
    </div>
  );
}
